import { useCallback, useState } from "react";

/**
 * The cursors of the pages walked so far. The current page is the last
 * entry (`undefined` for the first page); "next" pushes the cursor the API
 * returned and "previous" pops.
 *
 * `resetKey` names the list the cursors belong to: the filters, the view.
 * When it changes the stack goes back to the first page, since a cursor
 * is only valid for the list that produced it.
 */
export function useCursorPager(resetKey: string = "") {
    const [stack, setStack] = useState<(string | undefined)[]>([undefined]);

    // Reset during render rather than in an effect, so the page the new
    // key fetches is the first one and not a frame of the old cursor.
    const [lastKey, setLastKey] = useState(resetKey);
    if (lastKey !== resetKey) {
        setLastKey(resetKey);
        setStack([undefined]);
    }

    const cursor = stack[stack.length - 1];
    const pageNumber = stack.length;
    const hasPrevious = stack.length > 1;

    const next = useCallback((nextCursor: string) => {
        setStack((previous) => [...previous, nextCursor]);
    }, []);

    const previous = useCallback(() => {
        setStack((previous) => (previous.length > 1 ? previous.slice(0, -1) : previous));
    }, []);

    const reset = useCallback(() => {
        setStack([undefined]);
    }, []);

    return { cursor, pageNumber, hasPrevious, next, previous, reset };
}
